"use client";

import {
  forwardRef,
  useCallback,
  useId,
  type ElementType,
  type FormHTMLAttributes,
  type ReactNode,
  type ComponentType,
  type Ref
} from "react";

import {
  Box as ChakraBox,
  Flex as ChakraFlex,
  Stack as ChakraStack,
  HStack as ChakraHStack,
  VStack as ChakraVStack,
  Text as ChakraText,
  Heading as ChakraHeading,
  Grid as ChakraGrid,
  GridItem as ChakraGridItem,
  Wrap as ChakraWrap,
  WrapItem as ChakraWrapItem,
  SimpleGrid as ChakraSimpleGrid,
  Input as ChakraInput,
  Link as ChakraLink,
  Card as ChakraCard,
  Tag as ChakraTag,
  Menu as ChakraMenu,
  Portal,
  Dialog as ChakraDialog,
  useBreakpointValue,
  useDisclosure,
  useMediaQuery,
  Icon,
  type BoxProps as ChakraBoxProps,
  type FlexProps as ChakraFlexProps,
  type StackProps as ChakraStackProps,
  type TextProps as ChakraTextProps,
  type HeadingProps as ChakraHeadingProps,
  type GridProps as ChakraGridProps,
  type GridItemProps as ChakraGridItemProps,
  type WrapProps as ChakraWrapProps,
  type WrapItemProps as ChakraWrapItemProps,
  type SimpleGridProps as ChakraSimpleGridProps,
  type CardRootProps,
  type TagRootProps,
  type InputProps as ChakraInputProps,
  type LinkProps as ChakraLinkProps,
  type MenuRootProps,
  type HTMLChakraProps,
  type DialogRootProps
} from "@chakra-ui/react";

import { Tooltip } from "../tooltip/tooltip";
import { Dialog as DialogWithCloseIcon } from "../chakra-slots";
import { asSlotComponent } from "./v3-slot";
import { Image } from "../image/image";

const CardRoot = asSlotComponent(ChakraCard.Root);
const CardHeaderSlot = asSlotComponent(ChakraCard.Header);
const CardBodySlot = asSlotComponent(ChakraCard.Body);
const CardFooterSlot = asSlotComponent(ChakraCard.Footer);
const TagLabelSlot = asSlotComponent(ChakraTag.Label);
const TagCloseTrigger = asSlotComponent(ChakraTag.CloseTrigger);
const MenuTrigger = asSlotComponent(ChakraMenu.Trigger);
const MenuPositioner = asSlotComponent(ChakraMenu.Positioner);
const MenuContent = asSlotComponent(ChakraMenu.Content);
const MenuItemSlot = asSlotComponent(ChakraMenu.Item);
const DialogBackdrop = asSlotComponent(ChakraDialog.Backdrop);
const DialogPositioner = asSlotComponent(ChakraDialog.Positioner);
const DialogContent = asSlotComponent(ChakraDialog.Content);
const DialogHeader = asSlotComponent(ChakraDialog.Header);
const DialogBody = asSlotComponent(ChakraDialog.Body);
const DialogFooter = asSlotComponent(ChakraDialog.Footer);
const DialogCloseTriggerWithIcon = DialogWithCloseIcon.CloseTrigger;

type PolymorphicHtmlProps = {
  noValidate?: boolean;
  method?: string;
  action?: string;
  acceptCharset?: string;
  encType?: string;
  htmlFor?: string;
  href?: string;
  target?: string;
  rel?: string;
  type?: string;
  name?: string;
  autoComplete?: string;
  tabIndex?: number;
  onSubmit?: FormHTMLAttributes<HTMLElement>["onSubmit"];
};

export type BoxProps = ChakraBoxProps &
  Partial<PolymorphicHtmlProps> & {
    as?: ElementType | ComponentType<any>;
    ref?: Ref<any>;
  };
export const Box = forwardRef<HTMLDivElement, BoxProps>(
  function Box(props, ref) {
    return <ChakraBox ref={ref} {...(props as ChakraBoxProps)} />;
  }
);

export type FlexProps = ChakraFlexProps &
  Partial<PolymorphicHtmlProps> & {
    as?: ElementType | ComponentType<any>;
    ref?: Ref<any>;
  };
export const Flex = forwardRef<HTMLDivElement, FlexProps>(
  function Flex(props, ref) {
    return <ChakraFlex ref={ref} {...(props as ChakraFlexProps)} />;
  }
);

export type StackProps = ChakraStackProps &
  Partial<PolymorphicHtmlProps> & {
    as?: ElementType | ComponentType<any>;
    ref?: Ref<any>;
  };
export const Stack = forwardRef<HTMLDivElement, StackProps>(
  function Stack(props, ref) {
    return <ChakraStack ref={ref} {...(props as ChakraStackProps)} />;
  }
);
export const HStack = forwardRef<HTMLDivElement, StackProps>(
  function HStack(props, ref) {
    return <ChakraHStack ref={ref} {...(props as ChakraStackProps)} />;
  }
);
export const VStack = forwardRef<HTMLDivElement, StackProps>(
  function VStack(props, ref) {
    return <ChakraVStack ref={ref} {...(props as ChakraStackProps)} />;
  }
);

export type TextProps = Omit<ChakraTextProps, "size"> & {
  size?: string | Record<string, string> | Array<string | null>;
  ref?: Ref<HTMLParagraphElement>;
};
export const Text = forwardRef<HTMLParagraphElement, TextProps>(
  function Text(props, ref) {
    return <ChakraText ref={ref} {...(props as ChakraTextProps)} />;
  }
);

export type HeadingProps = Omit<ChakraHeadingProps, "size"> & {
  size?: string | Record<string, string> | Array<string | null>;
  ref?: Ref<HTMLHeadingElement>;
};
export const Heading = forwardRef<HTMLHeadingElement, HeadingProps>(
  function Heading(props, ref) {
    return <ChakraHeading ref={ref} {...(props as ChakraHeadingProps)} />;
  }
);

export interface CardProps extends Omit<
  CardRootProps,
  "as" | "direction" | "variant"
> {
  as?: ElementType;
  direction?: string;
  variant?: CardRootProps["variant"] | string;
  children?: ReactNode;
}

export function Card({
  as: AsComponent,
  direction,
  children,
  flexDirection,
  ...props
}: CardProps) {
  const cardProps = {
    flexDirection: direction ?? flexDirection,
    ...props
  };

  if (AsComponent) {
    return (
      <CardRoot asChild {...cardProps}>
        <AsComponent>{children}</AsComponent>
      </CardRoot>
    );
  }

  return <CardRoot {...cardProps}>{children}</CardRoot>;
}

function createCardSection(Slot: ReturnType<typeof asSlotComponent>) {
  return function CardSection({
    as: AsComponent,
    children,
    ...props
  }: HTMLChakraProps<"div"> & { as?: ElementType; children?: ReactNode }) {
    if (AsComponent) {
      return (
        <Slot asChild {...props}>
          <AsComponent>{children}</AsComponent>
        </Slot>
      );
    }

    return <Slot {...props}>{children}</Slot>;
  };
}

export const CardHeader = createCardSection(CardHeaderSlot);
export const CardBody = createCardSection(CardBodySlot);
export const CardFooter = createCardSection(CardFooterSlot);

export type TagProps = TagRootProps & {
  onClose?: () => void;
  children?: ReactNode;
};

export const Tag = forwardRef<HTMLDivElement, TagProps>(function Tag(
  { onClose, children, ...props },
  ref
) {
  return (
    <ChakraTag.Root ref={ref} {...(props as TagRootProps)}>
      <TagLabelSlot>{children}</TagLabelSlot>
      {onClose ? <TagCloseTrigger onClick={onClose} /> : null}
    </ChakraTag.Root>
  );
});

export { Tooltip };

export interface ModalProps extends Omit<DialogRootProps, "children"> {
  children?: ReactNode;
  onClose?: () => void;
}

export function Modal({
  open,
  onOpenChange,
  onClose,
  children,
  ...rest
}: ModalProps) {
  const handleOpenChange = useCallback(
    (details: { open: boolean }) => {
      onOpenChange?.(details);
      if (!details.open) {
        onClose?.();
      }
    },
    [onClose, onOpenChange]
  );

  return (
    <ChakraDialog.Root open={open} onOpenChange={handleOpenChange} {...rest}>
      {children}
    </ChakraDialog.Root>
  );
}

export function ModalOverlay(props: HTMLChakraProps<"div">) {
  return (
    <Portal>
      <DialogBackdrop {...props} />
    </Portal>
  );
}

export function ModalContent({
  containerProps,
  children,
  placement,
  ...props
}: HTMLChakraProps<"section"> & {
  containerProps?: HTMLChakraProps<"div">;
  placement?: "center" | "top" | "bottom";
  children?: ReactNode;
}) {
  return (
    <Portal>
      <DialogPositioner placement={placement} {...containerProps}>
        <DialogContent {...props}>{children}</DialogContent>
      </DialogPositioner>
    </Portal>
  );
}

export function ModalHeader(props: HTMLChakraProps<"div">) {
  return <DialogHeader {...props} />;
}

export function ModalBody(props: HTMLChakraProps<"div">) {
  return <DialogBody {...props} />;
}

export function ModalFooter(props: HTMLChakraProps<"footer">) {
  return <DialogFooter {...props} />;
}

export function ModalCloseButton(props: HTMLChakraProps<"button">) {
  return <DialogCloseTriggerWithIcon {...props} />;
}

export const Dialog = {
  Root: ChakraDialog.Root,
  Backdrop: DialogBackdrop,
  Positioner: DialogPositioner,
  Content: DialogContent,
  Header: DialogHeader,
  Body: DialogBody,
  Footer: DialogFooter,
  CloseTrigger: DialogCloseTriggerWithIcon,
  Title: asSlotComponent(ChakraDialog.Title)
};

export { Portal };

export {
  useDisclosure,
  useMediaQuery,
  useBreakpointValue,
  Icon,
  ChakraLink as ChakraLinkRaw,
  Image
};

export type GridProps = ChakraGridProps;
export function Grid(props: GridProps) {
  return <ChakraGrid {...props} />;
}

export type GridItemProps = ChakraGridItemProps;
export function GridItem(props: GridItemProps) {
  return <ChakraGridItem {...props} />;
}

export type WrapProps = ChakraWrapProps;
export function Wrap(props: WrapProps) {
  return <ChakraWrap {...(props as ChakraWrapProps)} />;
}

export type WrapItemProps = ChakraWrapItemProps;
export function WrapItem(props: WrapItemProps) {
  return <ChakraWrapItem {...(props as unknown as HTMLChakraProps<"div">)} />;
}

export type SimpleGridProps = ChakraSimpleGridProps;
export function SimpleGrid(props: SimpleGridProps) {
  return <ChakraSimpleGrid {...(props as ChakraSimpleGridProps)} />;
}

export type InputProps = ChakraInputProps;
export function Input(props: InputProps) {
  return <ChakraInput {...props} />;
}

export interface MenuButtonProps extends HTMLChakraProps<"button"> {
  as?: ElementType;
  children?: ReactNode;
}

export function MenuButton({ as: As, children, ...props }: MenuButtonProps) {
  if (As) {
    return (
      <MenuTrigger asChild>
        <As {...props}>{children}</As>
      </MenuTrigger>
    );
  }

  return <MenuTrigger {...props}>{children}</MenuTrigger>;
}

export interface MenuListProps extends HTMLChakraProps<"div"> {
  children?: ReactNode;
}

export function MenuList({ children, ...props }: MenuListProps) {
  return (
    <Portal>
      <MenuPositioner>
        <MenuContent {...props}>{children}</MenuContent>
      </MenuPositioner>
    </Portal>
  );
}

export interface MenuItemProps extends HTMLChakraProps<"div"> {
  children?: ReactNode;
  /** Zag requires a unique value per item for keyboard nav/typeahead. */
  value?: string;
}

export function MenuItem({ children, value, ...props }: MenuItemProps) {
  // Fall back to a stable generated value so keyboard nav/typeahead work even
  // when callers don't supply one (Zag registers items by value).
  const generatedValue = useId();
  return (
    <MenuItemSlot value={value ?? generatedValue} {...props}>
      {children}
    </MenuItemSlot>
  );
}

export type MenuProps = MenuRootProps;

export function Menu(props: MenuProps) {
  return <ChakraMenu.Root {...props} />;
}

export type LinkProps = ChakraLinkProps;
export function Link(props: LinkProps) {
  return <ChakraLink {...props} />;
}
